import {
  parseAllCeilingGrids,
  type ICeilingGridItem,
  type IDevice,
} from "@multiconsult-group/multiplacer-core";

export type CeilingHeightSpace = { name: string; ceilingGrids?: ICeilingGridItem[] };

export const createDeviceCeilingHeightResolver = (spaces: CeilingHeightSpace[]) => {
  const tilesByRoom = new Map(
    spaces.map((space) => [
      space.name,
      (space.ceilingGrids ?? []).flatMap((grid) => {
        const height =
          grid.lines?.flat().find((p) => Number.isFinite(p[2]))?.[2] ??
          grid.worldHeightM ??
          (grid.worldHeightMm != null ? grid.worldHeightMm / 1000 : undefined);
        if (height == null || !Number.isFinite(height)) return [];
        try {
          return parseAllCeilingGrids([grid])
            .flat(2)
            .flatMap((tile) =>
              tile.center && tile.boundingBox
                ? [{ center: tile.center, polygon: tile.boundingBox, height }]
                : []
            );
        } catch {
          // Incomplete grids cannot determine the device's mounting plane.
          return [];
        }
      }),
    ])
  );

  return (device: Pick<IDevice, "coordinate" | "roomNumber">): number | undefined => {
    const [x, y] = device.coordinate;
    let closestDistance = Infinity;
    let height: number | undefined;
    for (const tile of tilesByRoom.get(device.roomNumber) ?? []) {
      // Tiles are convex, rotated rectangles. Include points on their boundaries.
      let positive = false,
        negative = false;
      for (let i = 0; i < tile.polygon.length; i++) {
        const a = tile.polygon[i],
          b = tile.polygon[(i + 1) % tile.polygon.length];
        const cross = (b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]);
        positive ||= cross > 1e-8;
        negative ||= cross < -1e-8;
      }
      if (positive && negative) continue;
      const distance = Math.hypot(x - tile.center[0], y - tile.center[1]);
      if (distance < closestDistance) {
        closestDistance = distance;
        height = tile.height;
      }
    }
    return height;
  };
};
